// lib/modelist/fater3dState.ts
// Fater 3D's transitions over KernelData.fater3d, written through kernel.current.setFater3d(updater).
import type { Fater3DAttachment, Fater3DLight, Fater3DPlace, Fater3DRender, Fater3DSavedLight, Fater3DSite, Fater3DSiteFilters, Fater3DState, Fater3DViewJob, ModelistRunState, ModelistStep, ModelistView } from '#types/types';
import { clampLight, defaultLightOver, LIGHT_DEFAULTS, sameLight, savedLightOf, settingOf } from '#lib/modelist/light';
import { clampRender, RENDER_PRESETS } from '#lib/modelist/render';
import { clampPlace, meridianHours, placeOf, withZone, zoneFor } from '#lib/modelist/sun';
import { readLight, zoneName } from '../../scripts/modelist/light.mjs';
import { cleanView, viewPrint } from '../../scripts/modelist/views.mjs';
import { MODELIST_REFERENCES_PER_MESSAGE } from '../../scripts/modelist/references.mjs';
import { MODELIST_REFERENCE_MAX_BYTES, MODELIST_REFERENCES_TOTAL_BYTES, modelistReferenceExtension } from '#lib/modelist/references';

type OpenSite = Pick<Fater3DState, 'sceneOf' | 'sessionId'>;

/**
 * Whether the answer to a message sent from `sent` still concerns what is `open`: the same surface, on the site it
 * was sent from or on the one the answer opened. The site alone cannot tell two projects' scenes before their first
 * message, both without one.
 */
export const answerIsForOpenSite = (sent: OpenSite, open: OpenSite, answered?: string) =>
  sent.sceneOf === open.sceneOf && (open.sessionId === sent.sessionId || (answered !== undefined && open.sessionId === answered));

/** The server's refusal to delete a site, or a Studio project, whose Modelist is working (lib/modelist/modelist.server.ts). */
export const MODELIST_WORKING = 'The Modelist is working on this project. It can be deleted once it has stopped.';
/** What to tell the user of a failed deletion: that refusal alone, which the mutation client wraps in its own words. */
export const deletionFailure = (error: unknown, fallback: string) =>
  error instanceof Error && error.message.includes(MODELIST_WORKING) ? MODELIST_WORKING : fallback;

// The light and the sun's place as a site starts, before its project is read.
const UNLIT = { light: LIGHT_DEFAULTS, movedPlace: null, zoneNamed: false, landed: false };
/** What belongs to the site left: its place and its default light too, and the light and the place the user set over them. */
const cleared = () => ({ run: null, steps: {}, now: 0, error: null, views: {}, viewJobs: {}, attachments: [], place: null, projectLight: undefined, unconfirmed: {}, ...UNLIT });

export const setSites = (state: Fater3DState, sites: Fater3DSite[]): Fater3DState => ({ ...state, sites, sitesLoaded: true });
/** Another organisation or user: the list shown is no longer theirs until it is read again. */
export const setSiteFilters = (state: Fater3DState, filters: Fater3DSiteFilters): Fater3DState => ({ ...state, filters, sites: [], sitesLoaded: false });

/**
 * A site opens. One that had no session yet, a new project at its first message or a scene whose session was just
 * found, is the same site: the light the user set on it meanwhile stays.
 */
export const openSite = (state: Fater3DState, sessionId: string | null): Fater3DState => ({
  ...state, ...cleared(), view: 'site', sessionId,
  ...(state.view === 'site' && state.sessionId === null ? { light: state.light, movedPlace: state.movedPlace, zoneNamed: state.zoneNamed, landed: state.landed } : {}),
});

type Prepared = NonNullable<Fater3DState['prepared']>;

/**
 * A Studio project's scene as it comes on screen, its site still unknown; a message prepared for another project is
 * dropped. Already this project's, nothing changes: back from the canvas, the thread, its site and what is attached are
 * the ones left. Only another project's scene or the module, the one slice being theirs meanwhile, makes it start over.
 */
export const openScene = (state: Fater3DState, projectId: string): Fater3DState =>
  (state.sceneOf === projectId ? state
    : { ...state, ...cleared(), view: 'site', sceneOf: projectId, sessionId: null, prepared: state.prepared?.projectId === projectId ? state.prepared : null });

export const home = (state: Fater3DState): Fater3DState => ({ ...state, ...cleared(), view: 'dashboard', sceneOf: null, sessionId: null, prepared: null });

/** The latest request for a prepared message replaces any other, its picture still to come. */
export const prepare = (state: Fater3DState, pending: Prepared): Fater3DState => ({ ...state, prepared: pending });

/** A prepared message's picture landed. Ignored once another request replaced it. */
export const landPrepared = (state: Fater3DState, id: string, attachment: Fater3DAttachment): Fater3DState =>
  (state.prepared?.id === id ? { ...state, prepared: { ...state.prepared, attachment } } : state);

/** A prepared message whose picture could not be made, or whose project the user left. */
export const withdrawPrepared = (state: Fater3DState, id: string): Fater3DState => (state.prepared?.id === id ? { ...state, prepared: null } : state);

/**
 * Whether the scene of a prepared message's project is on screen, told by the scene itself as it shows and hides:
 * `sceneOf` cannot tell, it keeps the project after a return to the canvas.
 */
export const sceneShows = (state: Fater3DState, projectId: string, shown: boolean): Fater3DState =>
  (state.prepared?.projectId === projectId && state.prepared.sceneOpen !== shown ? { ...state, prepared: { ...state.prepared, sceneOpen: shown } } : state);

/** The prepared message a project's chat may deliver: its picture landed and its scene is open, so nothing clears what it attaches. */
export const deliverable = ({ prepared }: Fater3DState, projectId: string | undefined): Prepared | null =>
  (prepared && prepared.projectId === projectId && prepared.attachment && prepared.sceneOpen ? prepared : null);

/**
 * The chat box's draft once it takes a deliverable message's words, in front of what is typed, or null when it must
 * not take it: already taken (by id, `sceneShows` re-creates the object), or a send in flight, whose failure restores
 * the draft and attachments it left with and whose first success opens the site, clearing the attachments. The box
 * outlives a look at the canvas: an earlier message never sent, its words still whole at the head of the draft, is
 * the one the new message replaces (`replaces`, for `deliver` to take its picture out too); edited, they are the
 * user's own and stay, with their picture.
 */
export const draftTaking = (message: Prepared | null, taken: Pick<Prepared, 'id' | 'text'> | null, sending: boolean, draft: string): { draft: string; replaces: string | null } | null => {
  if (!message || message.id === taken?.id || sending) return null;
  const earlier = taken && draft.startsWith(taken.text) ? taken : null;
  return { draft: message.text + draft.slice(earlier?.text.length ?? 0), replaces: earlier?.id ?? null };
};

/**
 * The message leaves for the chat box, its picture first among the attachments, in place of the picture of the
 * message it replaces (a prepared picture carries its message's id). A box already full gives up its last file for
 * it, and says which. A second delivery of the same message changes nothing.
 */
export const deliver = (state: Fater3DState, message: Prepared, replaces: string | null = null): Fater3DState => {
  if (state.prepared !== message || !message.attachment) return state;
  const all = [message.attachment, ...state.attachments.filter((a) => a.id !== replaces)];
  const left = all.slice(MODELIST_REFERENCES_PER_MESSAGE);
  return { ...state, prepared: null, attachments: all.slice(0, MODELIST_REFERENCES_PER_MESSAGE),
    ...(left.length ? { error: `A message takes ${MODELIST_REFERENCES_PER_MESSAGE} files at most: the picture took the place of ${left.map((a) => a.name).join(', ')}.` } : {}) };
};

// Each read overlaps the last by a second: what comes back unchanged keeps its reference, so nothing redraws for it.
const same = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b);

export const absorb = (state: Fater3DState, { run, steps, now }: { run: ModelistRunState | null; steps: ModelistStep[]; now: number }): Fater3DState => {
  let next = state.steps;
  for (const s of steps) {
    if (same(next[s.key], s)) continue;
    if (next === state.steps) next = { ...state.steps };
    next[s.key] = s;
    // The server's copy of a message replaces the one drawn the moment it was sent.
    if (s.kind === 'user') for (const k of Object.keys(next)) if (k.startsWith('pending-') && next[k]!.label === s.label) delete next[k];
  }
  const nextRun = same(state.run, run) ? state.run : run;
  if (next === state.steps && nextRun === state.run && now === state.now) return state;
  return { ...state, run: nextRun, now, steps: next };
};

/** A message drawn in the thread as it is sent, before the server has it; unsend takes it back if refused. */
export const sent = (state: Fater3DState, step: ModelistStep): Fater3DState =>
  ({ ...state, steps: { ...state.steps, [step.key]: step }, run: state.run ? { ...state.run, status: 'running' } : null });

export const unsend = (state: Fater3DState, key: string): Fater3DState => {
  const next = { ...state.steps };
  delete next[key];
  return { ...state, steps: next };
};

export const tick = (state: Fater3DState): Fater3DState => (state.now ? { ...state, now: state.now + 1000 } : state);

/** The last step read: what a view's job, or a new view, counts from. */
export const lastSeq = (state: Pick<Fater3DState, 'steps'>) => Math.max(0, ...Object.values(state.steps).map((s) => s.seq));

/**
 * A view as it is saved under `name`: what the runner stores of it (cleanView), so the copy on screen is the one its
 * captures are judged against, with the step it was created or renamed after (since), which a later save keeps, and
 * the step of this save when it changes what the view shows (edited). Whole numbers, as the runner keeps them: the
 * user's own message, shown before it is stored, sits half a step past the last. `framed`: the view is saved from the
 * camera (Save view, Update camera and light), and takes the light in force with it; a change of its name or its
 * settings keeps the light it has, or none. Before the light is settled (`landed`: the project still unread, the user
 * having set none), the light in force is nobody's choice: the view takes none, and follows the project's.
 */
export const viewToSave = (state: Fater3DState, name: string, view: ModelistView, rename?: string, framed = false): ModelistView => {
  const kept = rename && rename !== name ? undefined : state.views[name];
  const at = Math.floor(lastSeq(state));
  const stored = (v: ModelistView) => (cleanView(v) as ModelistView | null) ?? v;
  const saved = stored({ ...view, ...(framed && state.landed ? { light: savedLightOf(state) } : {}), since: kept ? kept.since : at, edited: kept?.edited });
  return kept && viewPrint(saved) !== viewPrint(stored(kept)) ? { ...saved, edited: at } : saved;
};

export const setViewJob = (state: Fater3DState, name: string, job: Fater3DViewJob | null): Fater3DState => {
  const next = { ...state.viewJobs };
  if (job === null) delete next[name]; else next[name] = job;
  return { ...state, viewJobs: next };
};

export const setAttachments = (state: Fater3DState, next: Fater3DAttachment[] | ((list: Fater3DAttachment[]) => Fater3DAttachment[])): Fater3DState =>
  ({ ...state, attachments: typeof next === 'function' ? next(state.attachments) : next });

/**
 * Of the files picked, those a message takes beside what it already holds, in the order picked, and what to tell the
 * user of the others: a file of another kind or past the sizes, then a file past the number a message carries. Only
 * the files kept start an upload.
 */
export const attachable = <F extends { name: string; size: number }>(attached: Fater3DAttachment[], picked: F[]): { kept: F[]; refusal: string | null } => {
  let room = MODELIST_REFERENCES_TOTAL_BYTES - attached.reduce((sum, a) => sum + a.bytes, 0);
  const fitting = picked.filter((f) => {
    const fits = Boolean(modelistReferenceExtension(f.name)) && f.size <= MODELIST_REFERENCE_MAX_BYTES && f.size <= room;
    if (fits) room -= f.size;
    return fits;
  });
  const kept = fitting.slice(0, Math.max(0, MODELIST_REFERENCES_PER_MESSAGE - attached.length));
  const refusal = [
    fitting.length < picked.length ? `Attach images or design files (PDF, DWG, DXF, IFC, SKP, 3DM, RVT, OBJ, FBX, glTF, STL, SVG) of ${MODELIST_REFERENCE_MAX_BYTES / 1024 / 1024} MB at most, ${MODELIST_REFERENCES_TOTAL_BYTES / 1024 / 1024} MB in one message.` : '',
    kept.length < fitting.length ? `A message takes ${MODELIST_REFERENCES_PER_MESSAGE} files at most: ${kept.length} of ${fitting.length} attached. Send the others in another message.` : '',
  ].filter(Boolean).join(' ');
  return { kept, refusal: refusal || null };
};

/**
 * A message's attachments as they left, each with the address its upload ended on: what a refused send gives back to
 * the chat box. The box's own copy, read at the click, has no address yet for a file that was still uploading.
 */
export const sentAttachments = (picked: Fater3DAttachment[], urls: Array<string | null>): Fater3DAttachment[] =>
  picked.flatMap((a, i) => (urls[i] ? [{ ...a, url: urls[i]! }] : []));

const merged = <K extends 'render' | 'light'>(state: Fater3DState, key: K, patch: Partial<Fater3DState[K]>): Fater3DState => {
  const next = { ...state[key], ...patch };
  return (Object.keys(next) as Array<keyof Fater3DState[K]>).every((k) => next[k] === state[key][k]) ? state : { ...state, [key]: next };
};
export const setRender = (state: Fater3DState, patch: Partial<Fater3DRender>): Fater3DState => merged(state, 'render', clampRender(patch));
/** A change to the light. From then on it is the user's: the project's light, read later, is not put over it. */
export const setLight = (state: Fater3DState, patch: Partial<Fater3DLight>): Fater3DState => {
  const lit = merged(state, 'light', clampLight(patch));
  return lit === state || lit.landed ? lit : { ...lit, landed: true };
};

const samePlace = (a: Fater3DPlace | null, b: Fater3DPlace | null) => a === b || (a !== null && b !== null && a.lat === b.lat && a.lon === b.lon && a.north === b.north && a.zone === b.zone);

/**
 * The place the sun is computed over, moved by hand from the one in force and held on the globe; the project's own is
 * never written here. A zone the user names is kept as the engine spells it, and stays with the moved place wherever
 * it goes; one this reader does not know is not taken. Any other zone stays while the place keeps its hour of the
 * globe, and is given again by the rule (sun.ts zoneFor) once the place moves to another: whoever moves it, a place
 * taken from Paris to Bangkok leaves Paris's clock.
 */
export const movePlace = (state: Fater3DState, patch: Partial<Fater3DPlace>, browser: { zone: string; offset: number }): Fater3DState => {
  const from = placeOf(state);
  const kept = state.movedPlace !== null && state.zoneNamed;
  const named = patch.zone === undefined ? null : zoneName(patch.zone);
  const to = clampPlace({ ...from, ...patch, zone: from.zone });
  to.zone = named ?? (!kept && meridianHours(to.lon) !== meridianHours(from.lon) ? zoneFor(to.lon, browser) : from.zone);
  return samePlace(to, from) ? state : { ...state, movedPlace: to, zoneNamed: named ? true : kept, landed: true };
};

export const backToSite = (state: Fater3DState): Fater3DState => (state.movedPlace ? { ...state, movedPlace: null } : state);

/**
 * A saved light put in force: a view's as the view is opened, the project's as its site opens. By date and time it
 * comes with its place, which stands as a moved one unless it is the project's own; by its angles it leaves the place
 * alone. None, a view from before lights, changes nothing.
 */
export const applyLight = (state: Fater3DState, saved: Fater3DSavedLight | null | undefined): Fater3DState => {
  if (!saved) return state;
  const { place } = saved;
  const lit = setLight(state, settingOf(saved));
  // Over the place in force without a move, the default one of a project that has none included: not a move.
  const moved = place && !samePlace(place, placeOf({ place: lit.place, movedPlace: null })) ? place : null;
  return !place || samePlace(moved, lit.movedPlace) ? lit : { ...lit, movedPlace: moved, zoneNamed: false, landed: true };
};

/**
 * The light a site opens on: its project's default when it has one, the one just saved as it before the project
 * shows it, else the default light over the project's place.
 */
const openingLight = (state: Fater3DState): Fater3DSavedLight => state.unconfirmed.light ?? state.projectLight ?? defaultLightOver(state.place);

/**
 * The light a view without its own is captured under, and its picture judged against: the project's, else the
 * default one over the project's place, which the server hands the capture (modelist.server.ts). Undefined until the
 * project was read: nobody knows it, and no picture is judged on its light.
 */
export const captureLight = ({ projectLight, place }: Pick<Fater3DState, 'projectLight' | 'place'>): Fater3DSavedLight | undefined =>
  (projectLight === undefined ? undefined : projectLight ?? defaultLightOver(place));

/** A whole look in one go: its render settings, and with the Default preset the light and the sun's place as the site opens. */
export const applyPreset = (state: Fater3DState, preset: (typeof RENDER_PRESETS)[number]): Fater3DState => {
  const finished = setRender(state, preset.set);
  return preset.resetsLight ? applyLight(backToSite(finished), openingLight(finished)) : finished;
};

/** Whether the light and the sun's place are the ones the site opens on: what the Default preset, alone among them, sets. */
export const lightAtOpening = (state: Fater3DState) => sameLight(savedLightOf(state), openingLight(state)) && (state.light.sun === 'time' || state.movedPlace === null);

/**
 * A read of the open site's views, and of its project's light and place (the `views` answer, which is read as anything
 * may be). A saved place keeps the zone it names; the calibration's alone, which names none, is given its zone here.
 * An answer without a place keeps the one already held: the calibration's read can fail on its own
 * (modelist.server.ts sharedSite), and the sun would jump to the default place and back at the next read. The
 * project's default light is put in force by the first read of its site, unless the user set a light meanwhile, and
 * never again. A save the project now shows is no longer awaited, and a place saved is no longer a moved one. A
 * `stale` answer is the server saying it could not read the project just then (a commit replaced it between two
 * reads): it says nothing of the project, and nothing is taken from it.
 */
export const landViews = (state: Fater3DState, answer: { views?: Record<string, ModelistView>; light?: unknown; place?: unknown; stale?: boolean }, browser: { zone: string; offset: number }): Fater3DState => {
  if (answer.stale) return state;
  const { light = null, place } = readLight(answer) as { light?: Fater3DSavedLight; place?: Omit<Fater3DPlace, 'zone'> & { zone?: string } };
  const read = place ? withZone(place, browser) : state.place;
  const awaited = { ...state.unconfirmed };
  if (awaited.light && light && sameLight(light, awaited.light)) delete awaited.light;
  const placed = Boolean(awaited.place && place?.zone && samePlace(read, awaited.place));
  if (placed) delete awaited.place;
  const next: Fater3DState = {
    ...state, views: answer.views ?? {}, landed: true,
    place: samePlace(read, state.place) ? state.place : read,
    movedPlace: placed && samePlace(read, state.movedPlace) ? null : state.movedPlace,
    projectLight: same(light, state.projectLight) ? state.projectLight : light,
    unconfirmed: Object.keys(awaited).length === Object.keys(state.unconfirmed).length ? state.unconfirmed : awaited,
  };
  return state.landed ? next : applyLight(next, light);
};

type ProjectSave = keyof Fater3DState['unconfirmed'];

/**
 * A save of the project's light, or of its place, as it leaves: the light or the place in force, awaited from the
 * project. It is the project's own only once a read shows it (landViews): a runner from before saveLight never will.
 * A copy, so that the end of one save is told from another save of the same thing (saveDropped).
 */
export const saveSent = (state: Fater3DState, what: ProjectSave): Fater3DState =>
  ({ ...state, unconfirmed: { ...state.unconfirmed, ...(what === 'light' ? { light: { ...savedLightOf(state) } } : { place: { ...placeOf(state) } }) } });

/** A save that was refused, or that the project never showed: no longer awaited. A later save of the same is another. */
export const saveDropped = (state: Fater3DState, sent: Fater3DState['unconfirmed']): Fater3DState => {
  const awaited = { ...state.unconfirmed };
  for (const what of Object.keys(sent) as ProjectSave[]) if (awaited[what] === sent[what]) delete awaited[what];
  return Object.keys(awaited).length === Object.keys(state.unconfirmed).length ? state : { ...state, unconfirmed: awaited };
};

/**
 * The order of a site's reads of its project. Answers come back in any order: one issued before another that has
 * already landed is older than what is held, and is dropped (`lands`). What a save answers with is the project as the
 * save left it, so it takes its number when it arrives: every read issued before then is older.
 */
export const readOrder = () => {
  let issued = 0;
  let landed = 0;
  return { issue: () => (issued += 1), lands: (read: number) => read > landed && (landed = read) > 0 };
};

/** How often an open site reads its project again while it waits for it, and how many times: two minutes, the time a queued runner takes to start and commit with room to spare. */
export const REREAD = { everyMs: 5000, times: 24 } as const;
/**
 * What a waiting site does at its nth turn: read the project again, or stop. It waits for a save to show (`saving`),
 * which it gives up at the end, telling the user; or for a first read that has not landed, which it lets go quietly.
 */
export const rereadStep = (turn: number, saving: boolean): 'read' | 'give-up' | 'stop' => (turn <= REREAD.times ? 'read' : saving ? 'give-up' : 'stop');
